// clone-code-repo-into-src.ts
//
// v3: Clone 1 repo code vào src/<name> (KHÔNG submodule — workspace không git).
// Mỗi repo là git clone độc lập, có remote riêng, push/pull bình thường.
import { join } from "node:path";
import { ensureDir, pathExists } from "./filesystem-helpers.js";
import { git } from "./git-operations.js";
import { assertAllowedGitUrlScheme } from "./validate-git-url-scheme-allowlist.js";
import { addRepoToManifest, repoNameExists } from "./workspace-repos-manifest-store.js";

// Lỗi PRE-CLONE: repo/tên đã tồn tại trong workspace. Throw TRƯỚC git clone →
// repo cũ KHÔNG bị đụng. Caller dùng để PHÂN BIỆT: không được xóa src/<name> khi
// gặp lỗi này (tránh xóa nhầm repo cũ — mất data).
export class RepoAlreadyInWorkspaceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RepoAlreadyInWorkspaceError";
  }
}

// Tên repo: kebab/alphanumeric, không path separator, không trùng "pack".
export function validateRepoName(name: string): string | null {
  if (!name || name.trim().length === 0) return "Tên repo không được rỗng.";
  if (/[/\\]/.test(name) || name.includes("..")) return "Tên repo không được chứa '/', '\\', '..'.";
  if (name === "pack") return "Tên 'pack' bị giữ cho team-ai-pack.";
  return null;
}

// Suy tên repo từ git URL. "git@github.com:org/frontend.git" → "frontend".
export function inferRepoNameFromUrl(url: string): string {
  const trimmed = url.trim().replace(/\/+$/, "");
  const last = trimmed.split(/[/:]/).pop() ?? "";
  return last.replace(/\.git$/, "") || "repo";
}

export interface CloneRepoResult {
  name: string;
  path: string; // absolute
  url: string;
}

// Clone repo từ remote URL vào src/<name> của workspace.
// Validate tên + chống trùng. Ghi manifest.
export async function cloneCodeRepoIntoSrc(args: {
  workspaceRoot: string;
  url: string;
  name: string;
}): Promise<CloneRepoResult> {
  // PRE-CLONE validation errors (RepoAlreadyInWorkspaceError) → throw TRƯỚC khi
  // git clone chạy → repo/thư mục cũ KHÔNG bị đụng. Caller KHÔNG được xóa src/<name>
  // khi gặp lỗi này (nếu không sẽ xóa nhầm repo cũ — mất data).
  const nameError = validateRepoName(args.name);
  if (nameError) throw new RepoAlreadyInWorkspaceError(nameError);

  const srcDir = join(args.workspaceRoot, "src");
  await ensureDir(srcDir);
  const destPath = join(srcDir, args.name);
  const folderExists = await pathExists(destPath);
  const inManifest = await repoNameExists(args.workspaceRoot, args.name);

  // "Thực sự đã có" = folder tồn tại trong src/. Nếu folder TỒN TẠI → chặn (tránh đè).
  if (folderExists) {
    throw new RepoAlreadyInWorkspaceError(
      `Thư mục src/${args.name} đã tồn tại. Chọn tên khác hoặc gỡ trước (avatar remove repo).`,
    );
  }

  // SELF-HEAL: manifest có entry nhưng folder KHÔNG tồn tại (vd bị xóa ngoài / lỗi cũ)
  // → trạng thái lệch. Dọn entry rác khỏi manifest rồi cho add lại bình thường.
  if (inManifest) {
    const { removeRepoFromManifest } = await import("./workspace-repos-manifest-store.js");
    await removeRepoFromManifest(args.workspaceRoot, args.name);
  }

  // VULN-4a: chặn scheme lạ (ext::, file://) TRƯỚC khi truyền vào git clone —
  // git's `ext::` transport cho phép chạy shell tùy ý.
  assertAllowedGitUrlScheme(args.url);

  // git clone <url> src/<name> — từ đây nếu fail, src/<name> có thể bị tạo dở → caller dọn.
  await git(srcDir).clone(args.url, args.name);

  // Ghi manifest.
  await addRepoToManifest(args.workspaceRoot, {
    name: args.name,
    path: `src/${args.name}`,
    url: args.url,
    addedAt: new Date().toISOString(),
  });

  return { name: args.name, path: destPath, url: args.url };
}

// "Dự án mới": tạo repo LOCAL trong src/<name> + git init + add remote + commit
// baseline + push. KHÔNG clone (repo GitHub vừa tạo rỗng — clone về vô nghĩa + dễ
// fail SSH/timing). Repo mới sẵn sàng để dev code ngay, có remote để push.
export async function scaffoldNewRepoInSrc(args: {
  workspaceRoot: string;
  url: string; // remote URL của repo GitHub vừa tạo
  name: string;
}): Promise<CloneRepoResult> {
  const nameError = validateRepoName(args.name);
  if (nameError) throw new Error(nameError);
  if (await repoNameExists(args.workspaceRoot, args.name)) {
    throw new Error(`Repo "${args.name}" đã có trong workspace. Chọn tên khác hoặc gỡ trước.`);
  }

  const srcDir = join(args.workspaceRoot, "src");
  await ensureDir(srcDir);
  const destPath = join(srcDir, args.name);
  if (await pathExists(destPath)) {
    throw new Error(`Thư mục src/${args.name} đã tồn tại. Chọn tên khác.`);
  }

  await ensureDir(destPath);
  const g = git(destPath);
  await g.init();
  // README baseline để có gì commit + push (repo không rỗng hoàn toàn).
  const { writeTextAtomic } = await import("./filesystem-helpers.js");
  await writeTextAtomic(join(destPath, "README.md"), `# ${args.name}\n`);
  await g.add(".");
  await g.commit("chore: initial commit (avatar add repo — dự án mới)").catch(() => {});
  await g.addRemote("origin", args.url).catch(() => {});
  // Push lên remote vừa tạo. Best-effort: fail (vd remote đã có README) → vẫn giữ
  // repo local + remote, dev tự pull/push sau.
  await g.push(["-u", "origin", "HEAD"]).catch((err) => {
    // Push fail không block — repo local + remote đã link, dev xử lý sau.
    // (vd repo GitHub tạo với --add-readme → có commit khác → cần pull --rebase trước.)
    void err;
  });

  await addRepoToManifest(args.workspaceRoot, {
    name: args.name,
    path: `src/${args.name}`,
    url: args.url,
    addedAt: new Date().toISOString(),
  });

  return { name: args.name, path: destPath, url: args.url };
}

// Check folder có uncommitted changes không (P9: tránh clone-from-remote mất data).
// Trả porcelain output (rỗng = clean).
export async function folderDirtyStatus(folderPath: string): Promise<string> {
  try {
    const status = await git(folderPath).status();
    return status.isClean() ? "" : status.files.map((f) => `${f.path}`).join(", ");
  } catch {
    return "";
  }
}

// Đọc remote origin URL của 1 folder git (cho flow "thư mục đã có").
export async function readFolderRemoteUrl(folderPath: string): Promise<string | null> {
  try {
    const remotes = await git(folderPath).getRemotes(true);
    const origin = remotes.find((r) => r.name === "origin");
    return origin?.refs.fetch ?? origin?.refs.push ?? null;
  } catch {
    return null;
  }
}
